import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Dashboard 视口级 CSS「全屏」：与 AIGC `useCssFullScreen` 同类（非浏览器 Fullscreen API），
 * 通过 Portal 挂到 body，避免祖先 `transform` 等导致 `position:fixed` 无法盖住左侧菜单。
 *
 * 无需 Portal 的场景可继续使用 `@/pages/aigc/.../useCssFullScreen`。
 */
export interface IUseViewportCssFullScreenOptions {
  onFullScreenChange?: (isFullScreen: boolean) => void;
  /** 全屏时锁定 body 滚动，默认 true */
  lockBodyScroll?: boolean;
  /** Portal 挂载节点，默认 `document.body` */
  getPortalContainer?: () => HTMLElement | null;
}

export interface IUseViewportCssFullScreenResult {
  isFullScreen: boolean;
  requestFullScreen: () => void;
  exitFullScreen: () => void;
  toggleFullScreen: () => void;
  /** 非全屏时包裹图表的外层 ref（用于常规布局；由 ViewportFullscreenShell 写入） */
  inlineRootRef: React.RefObject<HTMLDivElement>;
  /** Portal 内层根节点 ref（进入全屏后可用，便于测量或联动） */
  fullScreenLayerRef: React.RefObject<HTMLDivElement>;
}

export function useViewportCssFullScreen(
  options?: IUseViewportCssFullScreenOptions,
): IUseViewportCssFullScreenResult {
  const { onFullScreenChange, lockBodyScroll = true } = options || {};

  const [isFullScreen, setIsFullScreen] = useState(false);
  const inlineRootRef = useRef<HTMLDivElement>(null);
  const fullScreenLayerRef = useRef<HTMLDivElement>(null);

  const requestFullScreen = useCallback(() => {
    setIsFullScreen(true);
    onFullScreenChange?.(true);
  }, [onFullScreenChange]);

  const exitFullScreen = useCallback(() => {
    setIsFullScreen(false);
    onFullScreenChange?.(false);
  }, [onFullScreenChange]);

  const toggleFullScreen = useCallback(() => {
    if (isFullScreen) {
      exitFullScreen();
    } else {
      requestFullScreen();
    }
  }, [isFullScreen, exitFullScreen, requestFullScreen]);

  useEffect(() => {
    if (!isFullScreen) return undefined;

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        exitFullScreen();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isFullScreen, exitFullScreen]);

  useEffect(() => {
    if (!isFullScreen || !lockBodyScroll) return undefined;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isFullScreen, lockBodyScroll]);

  return {
    isFullScreen,
    requestFullScreen,
    exitFullScreen,
    toggleFullScreen,
    inlineRootRef,
    fullScreenLayerRef,
  };
}
